import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchOverviewData, fetchUserClientGeoscope } from "../api/marketOverviewApi";
import { isSupersededResponse } from "../utils/isSupersededResponse";

export const fetchMarketOverviewData = createAsyncThunk(
  'marketOverview/fetchOverviewData',
  async ({ segment = 'both', platform = 'Total', userData, selectedWorkspace }, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const { lastFetched, apiData, cacheDuration = 30 * 60 * 1000 } = state.marketOverview;

      // Data still inside the cache window short-circuits the request
      if (apiData && lastFetched && (Date.now() - lastFetched < cacheDuration)) {
        return { data: apiData, fromCache: true };
      }

      const response = await fetchOverviewData(segment, platform, userData, selectedWorkspace);
      
      return { 
        data: response, 
        fromCache: false,
        fetchParams: { segment, platform }
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchMarketGeoscopeData = createAsyncThunk(
  'marketOverview/fetchGeoscopeData',
  async ({ userData, selectedWorkspace }, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const { geoscopeLastFetched, geoscopeData, geoscopeCacheDuration = 30 * 60 * 1000 } = state.marketOverview;
      
      // Data still inside the cache window short-circuits the request
      if (geoscopeData && geoscopeLastFetched && (Date.now() - geoscopeLastFetched < geoscopeCacheDuration)) {
        return { data: geoscopeData, fromCache: true };
      }

      const response = await fetchUserClientGeoscope("market_overview", userData, selectedWorkspace);
      
      return { 
        data: response, 
        fromCache: false 
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Initial state for Market Overview filters
const initialState = {
  // requestId of the most recent overview fetch; only its response lands.
  latestRequestId: null,

  // Selected regions/markets - array of string values
  selectedRegion: [],
  
  // Selected platform - string value
  selectedPlatform: 'Total',
  
  // Selected segment - string value  
  selectedSegment: 'both',
  
  // Selected franchise - array value
  selectedFranchise: ['All Franchises'],

  // Selected part-of-chain filter - string value ('both' | 'yes' | 'no')
  selectedIsChain: 'both',
  
  // Selected metric - string value
  selectedMetric: 'ONE Score',

  // Date range object with start and end timestamps
  dateRange: null,

  // Time-axis grain for the trend chart and evolution table ('month' | 'quarter' | 'year')
  period: 'month',
  
  // Processed regions and markets data for display
  selectedRegionsAndMarkets: {
    regions: [],     // Array of {key: string, title: string}
    markets: [],     // Array of {region: string, market: string, key: string}
    regionTotals: [], // Array of {region: string, title: string, key: string}
    parentToChild: {} // Object mapping region titles to child market titles
  },
  
  // Search text states
  platformSearchText: '',
  regionSearchText: '',
  
  // API data cache
  apiData: null,
  lastFetched: null,
  cacheDuration: 30 * 60 * 1000,
  lastFetchParams: null,
  
  // Geoscope data cache
  geoscopeData: null,
  geoscopeLastFetched: null,
  geoscopeCacheDuration: 30 * 60 * 1000,
  
  // Loading states
  loading: false,
  geoscopeLoading: false,
  error: null,
  geoscopeError: null,
  
  // Initialization state
  isInitialized: false,
};

const marketOverviewSlice = createSlice({
  name: "marketOverview",
  initialState,
  reducers: {
    // Set selected regions/markets
    setSelectedRegion: (state, action) => {
      state.selectedRegion = action.payload;
    },
    
    // Set selected platform
    setSelectedPlatform: (state, action) => {
      state.selectedPlatform = action.payload;
    },
    
    // Set selected segment
    setSelectedSegment: (state, action) => {
      state.selectedSegment = action.payload;
    },

    // Set selected franchise
    setSelectedFranchise: (state, action) => {
      state.selectedFranchise = action.payload;
    },

    // Set selected chain flag
    setSelectedIsChain: (state, action) => {
      state.selectedIsChain = action.payload;
    },
    
    // Set selected metric
    setSelectedMetric: (state, action) => {
      state.selectedMetric = action.payload;
    },
    
    // Set date range
    setDateRange: (state, action) => {
      state.dateRange = action.payload;
    },

    // Set the time-axis grain. The date range is cleared with it so the page
    // re-derives a range in the new grain from the next payload.
    setPeriod: (state, action) => {
      state.period = action.payload;
      state.dateRange = null;
    },
    
    // Set processed regions and markets data
    setSelectedRegionsAndMarkets: (state, action) => {
      state.selectedRegionsAndMarkets = action.payload;
    },
    
    // Set platform search text
    setPlatformSearchText: (state, action) => {
      state.platformSearchText = action.payload;
    },
    
    // Set region search text
    setRegionSearchText: (state, action) => {
      state.regionSearchText = action.payload;
    },
    
    // Clearing the fetch timestamps makes the next fetch bypass the cache
    invalidateCache: (state) => {
      state.lastFetched = null;
      state.geoscopeLastFetched = null;
    },
    
    setInitialized: (state, action) => {
      state.isInitialized = action.payload;
    },
    
    // Clear all filters - reset to initial state except loading/error and cached data
    clearAllMarketOverviewFilters: (state) => {
      state.selectedRegion = [];
      state.selectedPlatform = 'Total';
      state.selectedSegment = 'both';
      state.selectedFranchise = ['All Franchises'];
      state.selectedIsChain = 'both';
      state.selectedMetric = 'ONE Score';
      state.dateRange = null;
      state.period = 'month';
      state.selectedRegionsAndMarkets = {
        regions: [],
        markets: [],
        regionTotals: [],
        parentToChild: {}
      };
      state.platformSearchText = '';
      state.regionSearchText = '';
    },
    
    // Reset to global selection (useful for initialization)
    resetToGlobalSelection: (state) => {
      state.selectedRegion = ['global'];
      state.selectedPlatform = 'Total';
      state.selectedSegment = 'both';
      state.selectedFranchise = ['All Franchises'];
      state.selectedIsChain = 'both';
      state.selectedMetric = 'ONE Score';
      state.dateRange = null; // Reset date range when workspace changes
      state.period = 'month';
      state.selectedRegionsAndMarkets = {
        regions: [],
        markets: [],
        regionTotals: [],
        parentToChild: {}
      };
      state.platformSearchText = '';
      state.regionSearchText = '';
      state.isInitialized = false; // Reset initialization flag
    },
    
    // Set loading state
    setMarketOverviewLoading: (state, action) => {
      state.loading = action.payload;
    },
    
    // Set error state
    setMarketOverviewError: (state, action) => {
      state.error = action.payload;
    },
    
    // Clear error
    clearMarketOverviewError: (state) => {
      state.error = null;
      state.geoscopeError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch overview data
      .addCase(fetchMarketOverviewData.pending, (state, action) => {
        state.latestRequestId = action.meta?.requestId ?? null;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMarketOverviewData.fulfilled, (state, action) => {
        // Only the latest request lands, so a response for the workspace or
        // filters the user just left never overwrites the current view.
        if (isSupersededResponse(state.latestRequestId, action)) return;
        state.loading = false;
        const { data, fromCache, fetchParams } = action.payload;
        
        if (!fromCache) {
          state.apiData = data;
          state.lastFetched = Date.now();
          state.lastFetchParams = fetchParams;
        }
        state.error = null;
      })
      .addCase(fetchMarketOverviewData.rejected, (state, action) => {
        if (isSupersededResponse(state.latestRequestId, action)) return;
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch geoscope data
      .addCase(fetchMarketGeoscopeData.pending, (state) => {
        state.geoscopeLoading = true;
        state.geoscopeError = null;
      })
      .addCase(fetchMarketGeoscopeData.fulfilled, (state, action) => {
        state.geoscopeLoading = false;
        const { data, fromCache } = action.payload;
        
        if (!fromCache) {
          state.geoscopeData = data;
          state.geoscopeLastFetched = Date.now();
        }
        state.geoscopeError = null;
      })
      .addCase(fetchMarketGeoscopeData.rejected, (state, action) => {
        state.geoscopeLoading = false;
        state.geoscopeError = action.payload;
      });
  }
});

// Export actions
export const {
  setSelectedRegion,
  setSelectedPlatform,
  setSelectedSegment,
  setSelectedFranchise,
  setSelectedIsChain,
  setSelectedMetric,
  setDateRange,
  setPeriod,
  setSelectedRegionsAndMarkets,
  setPlatformSearchText,
  setRegionSearchText,
  invalidateCache,
  setInitialized,
  clearAllMarketOverviewFilters,
  resetToGlobalSelection,
  setMarketOverviewLoading,
  setMarketOverviewError,
  clearMarketOverviewError
} = marketOverviewSlice.actions;

// Selectors
export const selectSelectedRegion = (state) => state.marketOverview.selectedRegion;
export const selectSelectedPlatform = (state) => state.marketOverview.selectedPlatform;
export const selectSelectedSegment = (state) => state.marketOverview.selectedSegment;
export const selectSelectedFranchise = (state) => state.marketOverview.selectedFranchise;
export const selectSelectedIsChain = (state) => state.marketOverview.selectedIsChain;
export const selectSelectedMetric = (state) => state.marketOverview.selectedMetric;
export const selectDateRange = (state) => state.marketOverview.dateRange;
export const selectPeriod = (state) => state.marketOverview.period ?? 'month';
export const selectSelectedRegionsAndMarkets = (state) => state.marketOverview.selectedRegionsAndMarkets;
export const selectPlatformSearchText = (state) => state.marketOverview.platformSearchText;
export const selectRegionSearchText = (state) => state.marketOverview.regionSearchText;
export const selectApiData = (state) => state.marketOverview.apiData;
export const selectGeoscopeData = (state) => state.marketOverview.geoscopeData;
export const selectLoading = (state) => state.marketOverview.loading;
export const selectGeoscopeLoading = (state) => state.marketOverview.geoscopeLoading;
export const selectError = (state) => state.marketOverview.error;
export const selectIsInitialized = (state) => state.marketOverview.isInitialized;

// Combined selector for all filters
export const selectAllMarketOverviewFilters = (state) => ({
  selectedRegion: state.marketOverview.selectedRegion,
  selectedPlatform: state.marketOverview.selectedPlatform,
  selectedSegment: state.marketOverview.selectedSegment,
  selectedFranchise: state.marketOverview.selectedFranchise,
  selectedIsChain: state.marketOverview.selectedIsChain,
  selectedMetric: state.marketOverview.selectedMetric,
  dateRange: state.marketOverview.dateRange,
  period: state.marketOverview.period ?? 'month',
  selectedRegionsAndMarkets: state.marketOverview.selectedRegionsAndMarkets,
  platformSearchText: state.marketOverview.platformSearchText,
  regionSearchText: state.marketOverview.regionSearchText
});

// Selector: is the cached overview data still inside its cache window
export const selectIsDataFresh = (state) => {
  const { lastFetched, cacheDuration } = state.marketOverview;
  if (!lastFetched) return false;
  return (Date.now() - lastFetched) < cacheDuration;
};

export const selectIsGeoscopeDataFresh = (state) => {
  const { geoscopeLastFetched, geoscopeCacheDuration } = state.marketOverview;
  if (!geoscopeLastFetched) return false;
  return (Date.now() - geoscopeLastFetched) < geoscopeCacheDuration;
};

export default marketOverviewSlice.reducer; 